import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';
import '../styles/ExperienceModal.css'; 

const ExperienceModal = ({ show, onHide, onSave, onDelete, experience }) => {
  const [title, setTitle] = useState('');
  const [purposeAndMindset, setPurposeAndMindset] = useState('');
  const [keyMoments, setKeyMoments] = useState([]);
  const [personalGrowth, setPersonalGrowth] = useState('');
  const [usefulPrinciples, setUsefulPrinciples] = useState('');
  const [regretsAndLosses, setRegretsAndLosses] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [year, setYear] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);


  useEffect(() => {
    if (experience) {
      setTitle(experience.title || '');
      // Handle migration from old field names
      setPurposeAndMindset(experience.purposeAndMindset || experience.whatICameFor || '');
      // Handle both old string format and new array format for backwards compatibility
      if (typeof experience.keyMoments === 'string') {
        setKeyMoments(experience.keyMoments ? [experience.keyMoments] : []);
      } else {
        setKeyMoments(experience.keyMoments || []);
      }
      setPersonalGrowth(experience.personalGrowth || '');
      setUsefulPrinciples(experience.usefulPrinciples || '');
      setRegretsAndLosses(experience.regretsAndLosses || experience.regrets || '');
      setImageUrl(experience.imageUrl || '');
      setYear(experience.year || '');
      setRating(experience.rating || 0);
    } else {
      setTitle('');
      setPurposeAndMindset('');
      setKeyMoments([]);
      setPersonalGrowth('');
      setUsefulPrinciples('');
      setRegretsAndLosses('');
      setImageUrl('');
      setYear('');
      setRating(0);
    }
  }, [experience]);

  const addKeyMoment = () => {
    setKeyMoments([...keyMoments, '']);
  };

  const updateKeyMoment = (index, value) => {
    const updated = [...keyMoments];
    updated[index] = value;
    setKeyMoments(updated);
  };

  const removeKeyMoment = (index) => {
    setKeyMoments(keyMoments.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const experienceData = {
      title,
      purposeAndMindset,
      keyMoments: keyMoments.filter(moment => moment.trim() !== ''), // Remove empty moments
      personalGrowth,
      usefulPrinciples,
      regretsAndLosses,
      imageUrl,
      year: year ? parseInt(year) : null,
      rating
    };
    onSave(experienceData);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{experience ? 'Edit Experience' : 'Add New Experience'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-8">
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Year</Form.Label>
                <Form.Control type="number" placeholder="e.g., 2024" value={year} onChange={(e) => setYear(e.target.value)} />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>What was my main purpose/goal and what was my initial mindset before jumping into this new experience?</Form.Label>
            <Form.Control as="textarea" rows={3} value={purposeAndMindset} onChange={(e) => setPurposeAndMindset(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>What were the main highlights and wins I'll remember the most?</Form.Label>
            <div className="key-moments-list">
              {keyMoments.map((moment, index) => (
                <div key={index} className="key-moment-item mb-2">
                  <div className="d-flex align-items-center">
                    <Form.Control 
                      type="text" 
                      placeholder="Describe a key moment..."
                      value={moment}
                      onChange={(e) => updateKeyMoment(index, e.target.value)}
                      className="me-2"
                    />
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => removeKeyMoment(index)}
                      className="flex-shrink-0"
                    >
                      ×
                    </Button>
                  </div>
                </div>
              ))}
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={addKeyMoment}
                className="mt-2"
              >
+ Add key moment
              </Button>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>What are the elements of personal growth, insights, mindset shifts, and important lessons?</Form.Label>
            <Form.Control as="textarea" rows={3} value={personalGrowth} onChange={(e) => setPersonalGrowth(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Which principles from the program were especially useful before and during the unfolding?</Form.Label>
            <Form.Control as="textarea" rows={3} value={usefulPrinciples} onChange={(e) => setUsefulPrinciples(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>If any; are there any regrets or losses stemming from this experience?</Form.Label>
            <Form.Control as="textarea" rows={3} value={regretsAndLosses} onChange={(e) => setRegretsAndLosses(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Image URL (Optional)</Form.Label>
            <Form.Control type="text" placeholder="https://example.com/image.jpg" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Your Rating</Form.Label>
            <div>
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={28}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  color={(hoverRating || rating) >= star ? "#ffc107" : "#e4e5e9"}
                  style={{ cursor: 'pointer', marginRight: '5px', transition: 'color 0.2s' }}
                />
              ))}
            </div>
          </Form.Group>

        </Form>
      </Modal.Body>
      <Modal.Footer>
        {experience && (
          <Button variant="outline-danger" onClick={() => onDelete(experience.id)} className="me-auto">Delete</Button>
        )}
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSave}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExperienceModal;
